import { ClientEvents } from 'common/events/ClientEvents';
import { chain } from 'lodash';
import { ConsumerGroupOrderFieldsFragment } from 'src/gqlReactTypings.generated.d';
import { useOnMount } from 'src/shared/hooks/useOnMount';
import { useTracker } from 'src/shared/hooks/useTracker';
import { GroupOrderFoodItemRating, GroupOrderFoodItemRatings } from '../../GroupOrderModal/GroupOrderFoodItemRating';
import { RatingPercent } from '../constants';
import {
  AdditionalDishesNumber,
  Header,
  RateRecentGroupOrderModalBody,
  StyledRateRecentGroupOrderModal,
} from './styles';

interface RateRecentGroupOrderModalProps {
  groupOrder: ConsumerGroupOrderFieldsFragment;
  onClose: () => void;
  onRatingClick: (rating: { lineItemId: number; ratingPercent: RatingPercent }) => void;
}

export const RateRecentGroupOrderModal = ({
  groupOrder,
  onClose,
  onRatingClick,
}: RateRecentGroupOrderModalProps): JSX.Element | null => {
  const tracker = useTracker();

  const { deliveryDate, orders } = groupOrder;
  const deliveryDateString = new Date(deliveryDate).toLocaleString([], {
    day: 'numeric',
    month: 'short',
  });
  const lineItems = chain(orders)
    .flatMap((order) => order.lineItems)
    .filter(({ rating }) => !rating)
    .value();
  const lineItemsToRender = lineItems.slice(0, 3);
  const numAdditionalLineItems = lineItemsToRender.length - lineItems.length;

  useOnMount(() => {
    tracker.track(ClientEvents.RATE_RECENT_GROUP_ORDER_MODAL_SHOW, { groupOrderId: groupOrder.id });
  });

  return (
    <StyledRateRecentGroupOrderModal isFullScreenOnMobile={false} isShowing onClose={onClose}>
      <RateRecentGroupOrderModalBody>
        <Header>Welcome back! How was your order on {deliveryDateString}?</Header>
        <GroupOrderFoodItemRatings>
          {lineItemsToRender.map(({ id: lineItemId, foodItem }) => (
            <GroupOrderFoodItemRating
              foodItem={foodItem}
              key={lineItemId}
              onRatingClick={(ratingPercent) => {
                onRatingClick({ ratingPercent, lineItemId });
                tracker.track(ClientEvents.RATE_RECENT_GROUP_ORDER_MODAL_CLICK, {
                  groupOrderId: groupOrder.id,
                  ratingPercent,
                });
              }}
            />
          ))}
        </GroupOrderFoodItemRatings>

        {numAdditionalLineItems > 0 ? (
          <AdditionalDishesNumber>+{numAdditionalLineItems} dishes</AdditionalDishesNumber>
        ) : null}
      </RateRecentGroupOrderModalBody>
    </StyledRateRecentGroupOrderModal>
  );
};
